import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ErrorSeverity = "critical" | "high" | "medium" | "low";
export type ErrorCategory =
  | "api_error"
  | "network_error"
  | "rate_limit"
  | "js_error"
  | "render_error"
  | "payment_error"
  | "auth_error";
export type ErrorStatus = "new" | "acknowledged" | "investigating" | "resolved" | "ignored";

export interface StorefrontError {
  readonly id: string;
  readonly fingerprint: string;
  readonly message: string;
  readonly category: ErrorCategory;
  readonly severity: ErrorSeverity;
  readonly status: ErrorStatus;
  readonly source: string;
  readonly url: string;
  readonly http_status: number | null;
  readonly stack_trace: string | null;
  readonly metadata: Record<string, unknown>;
  readonly occurrence_count: number;
  readonly first_seen_at: string;
  readonly last_seen_at: string;
  readonly resolved_at: string | null;
  readonly resolved_by: string | null;
  readonly notes: string | null;
}

export interface ErrorListResponse {
  readonly errors: readonly StorefrontError[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
  readonly stats: {
    readonly total: number;
    readonly new_count: number;
    readonly critical_count: number;
    readonly today_count: number;
    readonly resolved_today: number;
  };
}

export interface ErrorListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly severity?: ErrorSeverity | null;
  readonly category?: ErrorCategory | null;
  readonly status?: ErrorStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
  readonly q?: string | null;
}

export interface ErrorDetailResponse {
  readonly error: StorefrontError;
  readonly timeline: readonly {
    readonly id: string;
    readonly action: string;
    readonly actor: string | null;
    readonly notes: string | null;
    readonly created_at: string;
  }[];
  readonly related: readonly {
    readonly id: string;
    readonly message: string;
    readonly severity: ErrorSeverity;
    readonly last_seen_at: string;
  }[];
}

export interface UpdateErrorPayload {
  readonly status?: ErrorStatus;
  readonly notes?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  );
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const errorsApi = {
  /** Fetch paginated storefront errors with optional filters. */
  list: (params: ErrorListParams = {}) => {
    const qs = toQueryString({
      page: params.page,
      limit: params.limit,
      severity: params.severity,
      category: params.category,
      status: params.status,
      date_from: params.date_from,
      date_to: params.date_to,
      q: params.q,
    });
    return adminFetch<ErrorListResponse>(`/api/admin/errors${qs}`);
  },

  /** Fetch a single error with timeline and related errors. */
  detail: (id: string) =>
    adminFetch<ErrorDetailResponse>(`/api/admin/errors/${id}`),

  /** Update error status or add notes. */
  update: (id: string, payload: UpdateErrorPayload) =>
    adminFetch<{ error: StorefrontError }>(`/api/admin/errors/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  /** Bulk update error statuses. */
  bulkUpdate: (ids: readonly string[], payload: UpdateErrorPayload) =>
    adminFetch<{ updated: number }>("/api/admin/errors/bulk", {
      method: "PATCH",
      body: JSON.stringify({ ids, ...payload }),
    }),

  /** Get error stats for the dashboard card. */
  stats: () =>
    adminFetch<{
      total: number;
      new_count: number;
      critical_count: number;
      high_count: number;
      today_count: number;
      resolved_today: number;
      trend: readonly { date: string; count: number }[];
    }>("/api/admin/errors/stats"),
} as const;
